import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, DatePicker, Radio, Select, Spin } from 'antd';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import moment from 'moment';
import api from 'api';
import Privileges from 'lib/constants/privileges';
import { userHasPrivileges } from 'lib/helpers/renderCondition';
import { HOUR_BUCKETS, bucketForHours } from './hourBuckets';
import { CONTACT_STATUSES, statusFor } from './contactStatuses';
import { WORKER_SCOPES, countCalls, filterHomes, homesInScope, isCallVisible } from './workerFilters';
import { isSingleDay, loadFilters, saveFilters, stepDay } from './savedFilters';
import { loadView, saveView } from './savedView';
import { groupByLocation } from './locationGroups';
import './style.css';

const MIAMI_DADE = [25.85, -80.25];

const hasLocation = (block) => block.latitude != null && block.longitude != null;

const addressOf = (block) => [block.address, block.address2, block.zipcode].filter(Boolean).join(', ');

const inspectionUrl = (block) => `/inspections/${block.inspection_id}`;

const startLabel = (block) => block.scheduled_date ? moment(block.scheduled_date).format('MMM D, h:mm A') : 'Not scheduled';

async function fetchBlocks([start, end]) {
    const [scheduled, unscheduled] = await Promise.all([
        api.get('/inspection_time_blocks', {
            is_scheduled: true,
            starts_by: end.clone().endOf('day').toISOString(),
            ends_from: start.clone().startOf('day').toISOString(),
        }),
        api.get('/inspection_time_blocks', {is_scheduled: false, sort: 'location'}),
    ]);

    return [...scheduled.body, ...unscheduled.body];
}

const homeName = (home) => [home.first_name, home.last_name].filter(Boolean).join(' ');

const homeAddress = (home) => [home.address, home.address2, home.city, home.state, home.zipcode].filter(Boolean).join(', ');

// The same people the inspection worker picker offers.
function fetchHomes() {
    return api.get('/user_home_addresses', {employed: true, assignable_to_inspections: true});
}

function homePopup(home) {
    const popup = document.createElement('div');

    const name = document.createElement('strong');
    name.textContent = homeName(home);
    popup.appendChild(name);

    const address = document.createElement('div');
    address.textContent = homeAddress(home);
    popup.appendChild(address);

    return popup;
}

function popupFor(block, history) {
    const popup = document.createElement('div');

    const title = document.createElement('a');
    title.textContent = block.site_name;
    title.href = inspectionUrl(block);
    title.onclick = (event) => {
        event.preventDefault();
        history.push(inspectionUrl(block));
    };
    popup.appendChild(title);

    [addressOf(block), startLabel(block), `${block.work_hours} hrs, ${statusFor(block.contact_status).label}`].forEach(text => {
        const line = document.createElement('div');
        line.textContent = text;
        popup.appendChild(line);
    });

    return popup;
}

const hoursColor = (block) => bucketForHours(block.work_hours).color;

const statusColor = (block) => statusFor(block.contact_status).color;

function pinIcon(block) {
    return L.divIcon({
        className: '',
        html: blockPinHtml(block),
        iconSize: [18, 18],
        iconAnchor: [9, 9],
    });
}

const HOME_PIN_HTML = '<span class="inspection-map__pin inspection-map__pin--home"></span>';

const HOME_ICON = L.divIcon({
    className: '',
    html: HOME_PIN_HTML,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
});

function blockPinHtml(block) {
    return `<span class="inspection-map__pin inspection-map__pin--status" style="background:${hoursColor(block)};--status-color:${statusColor(block)}"></span>`;
}

function groupIcon(count) {
    return L.divIcon({
        className: '',
        html: `<span class="inspection-map__group">${count}</span>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
    });
}

function blockItem(block, history) {
    return {
        latitude: block.latitude,
        longitude: block.longitude,
        order: block.scheduled_date ? moment(block.scheduled_date).valueOf() : Number.MAX_SAFE_INTEGER - 1,
        pinHtml: blockPinHtml(block),
        icon: () => pinIcon(block),
        details: () => popupFor(block, history),
    };
}

function homeItem(home) {
    return {
        latitude: home.latitude,
        longitude: home.longitude,
        order: Number.MAX_SAFE_INTEGER,
        pinHtml: HOME_PIN_HTML,
        icon: () => HOME_ICON,
        details: () => homePopup(home),
    };
}

// Scheduled calls by start time, then calls with no date, then homes.
function groupPopup(items) {
    const popup = document.createElement('div');

    const heading = document.createElement('strong');
    heading.textContent = `${items.length} at this location`;
    popup.appendChild(heading);

    [...items].sort((a, b) => a.order - b.order).forEach(item => {
        const row = document.createElement('div');
        row.className = 'inspection-map__group-row';

        const pin = document.createElement('span');
        pin.className = 'inspection-map__group-pin';
        pin.innerHTML = item.pinHtml;
        row.appendChild(pin);

        row.appendChild(item.details());
        popup.appendChild(row);
    });

    return popup;
}

function InspectionMap({ history, currentUser }) {
    const canSeeHomes = userHasPrivileges([Privileges.VIEW_USER_HOME_ADDRESSES], currentUser);

    const [saved] = useState(() => loadFilters(currentUser.id));
    const [restoredView] = useState(() => loadView(currentUser.id));

    const [range, setRange] = useState(saved.range);
    const [showAssigned, setShowAssigned] = useState(saved.showAssigned);
    const [showUnassigned, setShowUnassigned] = useState(saved.showUnassigned);
    const [showUnscheduled, setShowUnscheduled] = useState(saved.showUnscheduled);
    const [blocks, setBlocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [homes, setHomes] = useState([]);
    const [homesError, setHomesError] = useState(null);
    const [showHomes, setShowHomes] = useState(saved.showHomes);
    const [workerScope, setWorkerScope] = useState(saved.workerScope);
    const [workerIds, setWorkerIds] = useState(canSeeHomes ? saved.workerIds : []);
    const [hiddenHours, setHiddenHours] = useState(saved.hiddenHours);
    const [autoZoom, setAutoZoom] = useState(saved.autoZoom);

    const canvas = useRef(null);
    const map = useRef(null);
    const pins = useRef(null);

    useEffect(() => {
        map.current = restoredView
            ? L.map(canvas.current).setView(restoredView.center, restoredView.zoom)
            : L.map(canvas.current).setView(MIAMI_DADE, 10);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; OpenStreetMap contributors',
        }).addTo(map.current);
        pins.current = L.layerGroup().addTo(map.current);
        map.current.on('moveend', () => {
            const {lat, lng} = map.current.getCenter();
            saveView(currentUser.id, {center: [lat, lng], zoom: map.current.getZoom()});
        });

        // Leaflet keeps the size it first measured, and on a fresh load that is before the side
        // menu takes its width, which would put the saved center off the visible middle.
        const resizes = new ResizeObserver(() => map.current.invalidateSize());
        resizes.observe(canvas.current);

        return () => {
            resizes.disconnect();
            map.current.remove();
        };
    }, [currentUser.id, restoredView]);

    useEffect(() => {
        let stale = false;
        setLoading(true);
        setError(null);

        fetchBlocks(range)
            .then(results => {
                if(!stale) {
                    setBlocks(results);
                }
            })
            .catch(() => {
                if(!stale) {
                    setBlocks([]);
                    setError("Couldn't load inspections.");
                }
            })
            .finally(() => {
                if(!stale) {
                    setLoading(false);
                }
            });

        return () => { stale = true; };
    }, [range]);

    useEffect(() => {
        saveFilters(currentUser.id, {range, workerScope, workerIds, showAssigned, showUnassigned, showUnscheduled, showHomes, hiddenHours, autoZoom});
    }, [currentUser.id, range, workerScope, workerIds, showAssigned, showUnassigned, showUnscheduled, showHomes, hiddenHours, autoZoom]);

    useEffect(() => {
        if(!canSeeHomes) {
            return undefined;
        }

        let stale = false;
        fetchHomes().then(response => {
            if(stale) {
                return;
            }
            if(response.success) {
                // A remembered person may have left or changed role; a scope change clears the picks anyway.
                const known = homesInScope(response.body, saved.workerScope).map(home => home.user_id);
                setHomes(response.body);
                setWorkerIds(ids => ids.filter(id => known.indexOf(id) !== -1));
            } else {
                setHomesError("Couldn't load inspector homes.");
                setWorkerIds([]);
            }
        });

        return () => { stale = true; };
    }, [canSeeHomes, saved.workerScope]);

    const changeWorkerScope = (scope) => {
        setWorkerScope(scope);
        setWorkerIds([]);
    };

    const toggleHours = (key, shown) => {
        setHiddenHours(hidden => shown ? hidden.filter(other => other !== key) : [...hidden, key]);
    };

    const visible = blocks.filter(block => isCallVisible(block, {showAssigned, showUnassigned, showUnscheduled, hiddenHours, scope: workerScope, userIds: workerIds}));
    const counts = countCalls(visible);
    const placed = visible.filter(hasLocation);
    const unplaced = visible.filter(block => !hasLocation(block));
    const shownHomes = showHomes ? filterHomes(homes, workerScope, workerIds) : [];
    const placedHomes = shownHomes.filter(hasLocation);
    const unplacedHomes = shownHomes.filter(home => !hasLocation(home));

    useEffect(() => {
        pins.current.clearLayers();

        groupByLocation([...placed.map(block => blockItem(block, history)), ...placedHomes.map(homeItem)]).forEach(group => {
            const [first] = group;
            const position = [first.latitude, first.longitude];

            if(group.length === 1) {
                L.marker(position, {icon: first.icon()})
                    .bindPopup(first.details())
                    .addTo(pins.current);
            } else {
                L.marker(position, {icon: groupIcon(group.length), zIndexOffset: 1000})
                    .bindPopup(groupPopup(group), {minWidth: 260, maxHeight: 320})
                    .addTo(pins.current);
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blocks, showAssigned, showUnassigned, showUnscheduled, hiddenHours, homes, showHomes, workerScope, workerIds]);

    // Coming back to a saved view keeps it through the page's own loading; the first change
    // to a filter or to Auto-zoom hands the map back to auto-zoom.
    const fitFilters = JSON.stringify([autoZoom, range, showAssigned, showUnassigned, showUnscheduled, hiddenHours, showHomes, workerScope, workerIds]);
    const filtersHoldingView = useRef(restoredView ? fitFilters : null);

    useEffect(() => {
        if(filtersHoldingView.current === fitFilters) {
            return;
        }
        filtersHoldingView.current = null;

        const everything = [...placed, ...placedHomes];
        if(autoZoom && everything.length > 0) {
            map.current.fitBounds(L.latLngBounds(everything.map(item => [item.latitude, item.longitude])).pad(0.2));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoZoom, blocks, showAssigned, showUnassigned, showUnscheduled, hiddenHours, homes, showHomes, workerScope, workerIds]);

    return <div className="inspection-map">
        <h1>Inspection Map</h1>

        <div className="inspection-map__controls">
            <DatePicker.RangePicker value={range} allowClear={false} onChange={setRange} format="MMM D, YYYY" />
            {isSingleDay(range) ? <Button.Group>
                <Button icon="left" title="Previous day" aria-label="Previous day" onClick={() => setRange(stepDay(range, -1))} />
                <Button icon="right" title="Next day" aria-label="Next day" onClick={() => setRange(stepDay(range, 1))} />
            </Button.Group> : null}
            <Radio.Group size="small" value={workerScope} onChange={e => changeWorkerScope(e.target.value)}>
                <Radio.Button value={WORKER_SCOPES.ALL}>All{canSeeHomes ? ` (${homes.length})` : ''}</Radio.Button>
                <Radio.Button value={WORKER_SCOPES.INSPECTORS}>Inspectors{canSeeHomes ? ` (${homesInScope(homes, WORKER_SCOPES.INSPECTORS).length})` : ''}</Radio.Button>
            </Radio.Group>
            {canSeeHomes ? <Select
                className="inspection-map__home-select"
                mode="multiple"
                size="small"
                allowClear
                placeholder="Pick specific people"
                optionFilterProp="children"
                value={homes.length > 0 ? workerIds : []}
                onChange={setWorkerIds}
            >
                {homesInScope(homes, workerScope).map(home =>
                    <Select.Option key={home.user_id} value={home.user_id}>{homeName(home)}</Select.Option>
                )}
            </Select> : null}
            <Checkbox checked={showUnassigned} onChange={e => setShowUnassigned(e.target.checked)}>Unassigned calls</Checkbox>
            <Checkbox checked={showAssigned} onChange={e => setShowAssigned(e.target.checked)}>Assigned calls</Checkbox>
            <Checkbox checked={showUnscheduled} onChange={e => setShowUnscheduled(e.target.checked)}>Not scheduled</Checkbox>
            {canSeeHomes ? <Checkbox checked={showHomes} onChange={e => setShowHomes(e.target.checked)}>Inspector homes</Checkbox> : null}
            <Checkbox checked={autoZoom} onChange={e => setAutoZoom(e.target.checked)}>Auto-zoom</Checkbox>
            {loading ? <Spin size="small" /> : <span>{visible.length} calls, {placed.length} on map</span>}
        </div>

        {error ? <Alert type="error" message={error} showIcon style={{marginBottom: 12}} /> : null}
        {homesError ? <Alert type="error" message={homesError} showIcon style={{marginBottom: 12}} /> : null}

        <div className="inspection-map__body">
            <div className="inspection-map__canvas" ref={canvas} />

            <div className="inspection-map__side">
                <div className="inspection-map__counts">
                    <h3>Showing</h3>
                    <div>{counts.unassigned} unassigned</div>
                    <div>{counts.assigned} assigned</div>
                    <div>{counts.unscheduled} not scheduled</div>
                    {canSeeHomes && showHomes ? <div>{shownHomes.length} homes</div> : null}
                </div>

                <h3>Center: assigned hours</h3>
                {HOUR_BUCKETS.map(bucket =>
                    <div className="inspection-map__legend-row" key={bucket.key}>
                        <Checkbox checked={hiddenHours.indexOf(bucket.key) === -1} onChange={e => toggleHours(bucket.key, e.target.checked)}>
                            <span className="inspection-map__pin inspection-map__hour-pin" style={{background: bucket.color}} />
                            {bucket.label}
                        </Checkbox>
                    </div>
                )}
                <h3 className="inspection-map__legend-heading">Outer ring: confirmation</h3>
                {CONTACT_STATUSES.map(status =>
                    <div className="inspection-map__legend-row" key={status.key}>
                        <span className="inspection-map__pin inspection-map__pin--status" style={{background: '#fff', '--status-color': status.color}} />
                        {status.label}
                    </div>
                )}
                {canSeeHomes ? <div className="inspection-map__legend-row">
                    <span className="inspection-map__pin inspection-map__pin--home" />
                    Inspector home
                </div> : null}
                <div className="inspection-map__legend-row">
                    <span className="inspection-map__group">2</span>
                    Several at one location
                </div>

                {unplaced.length > 0 ? <div className="inspection-map__unplaced">
                    <h3>Not on map ({unplaced.length})</h3>
                    {unplaced.map(block =>
                        <div className="inspection-map__unplaced-row" key={block.id}>
                            <span className="inspection-map__pin inspection-map__pin--status" style={{background: hoursColor(block), '--status-color': statusColor(block)}} />
                            <div>
                                <Link to={inspectionUrl(block)}>{block.site_name}</Link>
                                <div>{addressOf(block)}</div>
                                <div>{startLabel(block)}, {block.work_hours} hrs, {statusFor(block.contact_status).label}</div>
                            </div>
                        </div>
                    )}
                </div> : null}

                {unplacedHomes.length > 0 ? <div className="inspection-map__unplaced">
                    <h3>Homes not on map ({unplacedHomes.length})</h3>
                    {unplacedHomes.map(home =>
                        <div className="inspection-map__unplaced-row" key={home.user_id}>
                            <span className="inspection-map__pin inspection-map__pin--home" />
                            <div>
                                <Link to={`/users/${home.user_id}`}>{homeName(home)}</Link>
                                <div>{homeAddress(home)}</div>
                            </div>
                        </div>
                    )}
                </div> : null}
            </div>
        </div>
    </div>;
}

export default withRouter(InspectionMap);
